/** Shared plot bounds for the revenue chart (design-space px). */
export const REVENUE_PLOT = {
  top: 123,
  bottom: 315,
} as const

export const revenueValueToY = (valueK: number, maxK: number) =>
  REVENUE_PLOT.bottom -
  (valueK / maxK) * (REVENUE_PLOT.bottom - REVENUE_PLOT.top)

/** Formats raw EUR currency values for axis labels and tooltips. */
const stripTrailingZeros = (s: string) =>
  s.replace(/(?:\.0+|(?<=\.[0-9]*?)0+)$/, '')

export const formatRevenueK = (value: number) => {
  if (!Number.isFinite(value)) return '€0'

  const abs = Math.abs(value)

  if (abs < 1000) {
    const decimals = Math.abs(Math.round(value) - value) < 1e-9 ? 0 : 2
    return stripTrailingZeros(value.toFixed(decimals))
  }

  if (abs < 1_000_000) {
    const n = value / 1000
    const rounded =
      Math.abs(Math.round(n) - n) < 1e-9
        ? n.toFixed(0)
        : Math.abs(Math.round(n * 10) - n * 10) < 1e-9
          ? n.toFixed(1)
          : n.toFixed(2)

    return `${stripTrailingZeros(rounded)}k`
  }

  const m = value / 1_000_000
  const roundedM =
    Math.abs(Math.round(m) - m) < 1e-9
      ? m.toFixed(0)
      : Math.abs(Math.round(m * 10) - m * 10) < 1e-9
        ? m.toFixed(1)
        : m.toFixed(2)

  return `${stripTrailingZeros(roundedM)}M`
}

/** Compute a "nice" upper bound above the provided max value. */
export const niceRevenueMax = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return 1000

  const magnitude = 10 ** Math.floor(Math.log10(value))
  const normalized = value / magnitude

  const nice =
    normalized <= 1
      ? 1
      : normalized <= 2
        ? 2
        : normalized <= 5
          ? 5
          : 10

  return nice * magnitude
}

export const buildRevenueAxisLabels = (maxValue: number) =>
  [maxValue, maxValue * 0.75, maxValue * 0.5, maxValue * 0.25, 0].map((v) =>
    formatRevenueK(Number.isFinite(v) ? v : 0),
  )

/** Smooth cubic-bezier style path through chart points (Catmull-Rom → Bezier). */
export const buildSmoothLinePath = (
  points: { x: number; y: number }[],
) => {
  if (points.length === 0) return ''
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`

  const path: string[] = [`M ${points[0].x} ${points[0].y}`]

  for (let index = 0; index < points.length - 1; index += 1) {
    const current = points[index]
    const next = points[index + 1]
    const previous = points[index - 1] ?? current
    const after = points[index + 2] ?? next

    const cp1x = current.x + (next.x - previous.x) / 6
    const cp1y = current.y + (next.y - previous.y) / 6
    const cp2x = next.x - (after.x - current.x) / 6
    const cp2y = next.y - (after.y - current.y) / 6

    path.push(
      `C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y}`,
    )
  }

  return path.join(' ')
}

export type DonutSegment = {
  id: string
  percent: number
  color: string
}

/** Converts slice percentages into stroke-dash segments on a circle. */
export const buildDonutSegments = (
  slices: DonutSegment[],
  circumference: number,
) => {
  let offset = 0

  return slices.map((slice) => {
    const length = (slice.percent / 100) * circumference
    const segment = { ...slice, length, offset }
    offset += length
    return segment
  })
}

/** Eased number tween for center totals and counters. */
export const tweenNumber = (
  from: number,
  to: number,
  progress: number,
  ease: (t: number) => number = (t) => 1 - (1 - t) ** 3,
) => Math.round(from + (to - from) * ease(progress))